import React, { useEffect, useMemo,useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import CloseIcon from "@mui/icons-material/Close";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import {
  getAllUserAction,
  getAllZonesAction,
  createZoneAction,
  clearCreateZoneAction,
  getZoneByIdAction,
  updateZoneAction,
  clearUpdateZoneAction,
  clearGetZoneByIdAction,
} from "../../../store/actions";

const AddZone = ({ open, onClose, isEdit = false, zoneId = "" }) => {
  const dispatch = useDispatch();
  const [duplicateError, setDuplicateError] = useState("");

  const { successData: usersData, loading: usersLoading } = useSelector((state) => state.getAllUserReducer || {});
  const { successData: createSuccess, loading: createLoading, error: createError, errorInfo: createErrorInfo } = useSelector((state) => state.createZoneReducer || {});
  const { successData: zoneByIdData, loading: zoneByIdLoading, error: zoneByIdError } = useSelector((state) => state.getZoneByIdReducer || {});
  const { successData: updateSuccess, loading: updateLoading, error: updateError, errorInfo: updateErrorInfo } = useSelector((state) => state.updateZoneReducer || {});
  const { successData: zonesData, loading: zonesLoading } = useSelector(
  (state) => state.getAllZonesReducer || {}
);

  const users = useMemo(() => {
    if (Array.isArray(usersData)) return usersData;
    if (usersData && usersData.data && Array.isArray(usersData.data.users)) return usersData.data.users;
    return [];
  }, [usersData]);

  const zones = useMemo(() => {
  if (Array.isArray(zonesData)) return zonesData;
  if (zonesData?.data?.zones && Array.isArray(zonesData.data.zones)) {
    return zonesData.data.zones;
  }
  return [];
}, [zonesData]);

  const zmUsers = useMemo(() => {
    const list = Array.isArray(users) ? users : [];
    return list.filter((u) => {
      const role = (u.role || u.roleType || u.userRole || u.role_name || "").toString().toUpperCase();
      return role === "ZM";
    });
  }, [users]);

  const zoneSchema = Yup.object({
    zone_name: Yup.string().trim().required("Zone name is required"),
  });

  const normalizeZoneName = (name) =>
  (name || "").replace(/\s+/g, "").toLowerCase();

  const formik = useFormik({
    initialValues: {
      zone_name: "",
      incharge_user_id: "",
    },
    validationSchema: zoneSchema,
    enableReinitialize: false,
   onSubmit: (values) => {
  const newNameNorm = normalizeZoneName(values.zone_name);

  const duplicate = zones.some((z) => {
    const zName = z?.zoneName || z?.zone_name || z?.name || "";
    const zId = z?.zoneId || z?.id || z?.zone_id;

    if (isEdit && String(zId) === String(zoneId)) return false;

    return normalizeZoneName(zName) === newNameNorm;
  });

  if (duplicate) {
    setDuplicateError("Zone with this name already exists!");
    return;
  }

  setDuplicateError("");

  const payload = {
    zone_name: values.zone_name,
    incharge_user_id: values.incharge_user_id ? values.incharge_user_id : null,
  };

  if (isEdit && zoneId) {
    dispatch(updateZoneAction({ ...payload, endPoint: `zone/${zoneId}` }));
  } else {
    dispatch(createZoneAction(payload));
  }
},
  });

  // Prevent duplicate getAllUserAction dispatches while dialog is open
  const didFetchUsers = React.useRef(false);
  useEffect(() => {
    if (!open) {
      didFetchUsers.current = false;
      setDuplicateError("");
      return;
    }
    if (!didFetchUsers.current) {
      dispatch(getAllUserAction());
      dispatch(getAllZonesAction());
      didFetchUsers.current = true;
    }
    if (isEdit && zoneId) {
      dispatch(getZoneByIdAction({ endPoint: `zone/${zoneId}` }));
    } else {
      dispatch(clearGetZoneByIdAction());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, zoneId, dispatch]);

  useEffect(() => {
    if (isEdit && zoneByIdData && !zoneByIdLoading && !zoneByIdError) {
      const z = zoneByIdData?.data?.zone || zoneByIdData?.data || zoneByIdData;
      const prefill = {
        zone_name: z?.zone_name || z?.zoneName || z?.name || "",
        incharge_user_id: z?.incharge_user_id || z?.inchargeUserId || z?.userId || "",
      };
      formik.setValues(prefill);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, zoneByIdData, zoneByIdLoading, zoneByIdError]);

  useEffect(() => {
  if (!formik.values.zone_name) {
    setDuplicateError("");
    return;
  }

  const newNameNorm = normalizeZoneName(formik.values.zone_name);

  const duplicate = zones.some((z) => {
    const zName = z?.zoneName || z?.zone_name || z?.name || "";
    const zId = z?.zoneId || z?.id || z?.zone_id;

    if (isEdit && String(zId) === String(zoneId)) return false;

    return normalizeZoneName(zName) === newNameNorm;
  });

  setDuplicateError(duplicate ? "Zone with this name already exists!" : "");
}, [formik.values.zone_name, zones, isEdit, zoneId]);

  useEffect(() => {
    if (createSuccess) {
      toast.success("Zone created successfully!");
      formik.resetForm();
      onClose?.();
      dispatch(clearCreateZoneAction());
      dispatch(getAllZonesAction());
    }
    if (createError) {
      toast.error(createErrorInfo || "Failed to create zone");
      dispatch(clearCreateZoneAction());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createSuccess, createError, createErrorInfo]);

  useEffect(() => {
    if (updateSuccess) {
      toast.success("Zone updated successfully!");
      formik.resetForm();
      onClose?.();
      dispatch(clearUpdateZoneAction());
      dispatch(clearGetZoneByIdAction());
      dispatch(getAllZonesAction());
    }
    if (updateError) {
      toast.error(updateErrorInfo || "Failed to update zone");
      dispatch(clearUpdateZoneAction());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateSuccess, updateError, updateErrorInfo]);

  const handleCancel = () => {
    onClose?.();
    formik.resetForm();
    dispatch(clearGetZoneByIdAction());
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 4,
            p: { xs: 2, sm: 4 },
          },
        },
      }}
    >
      <IconButton onClick={handleCancel} sx={{ position: "absolute", top: 16, right: 16 }}>
        <CloseIcon />
      </IconButton>

      <DialogTitle sx={{ p: 0, mb: 1 }}>
        <Typography fontSize={28} fontWeight={700}>Zone Information</Typography>
        <Typography fontSize={14} color="text.secondary">{isEdit ? "Update Zone" : "Create new Zone"}</Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 0, mt: 3 }}>
        <form onSubmit={formik.handleSubmit}>
          <Box mb={2}>
            <Typography fontSize={14} fontWeight={600} mb={1}>Zone Name</Typography>
            <TextField
              name="zone_name"
              value={formik.values.zone_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              size="small"
              placeholder="Enter Zone"
              error={!!duplicateError || (formik.touched.zone_name && Boolean(formik.errors.zone_name))}
              helperText={duplicateError || (formik.touched.zone_name && formik.errors.zone_name)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
            />
          </Box>

          <Box mb={4}>
            <Typography fontSize={14} fontWeight={600} mb={1}>Incharge User</Typography>
            <Autocomplete
              options={zmUsers}
              getOptionLabel={(option) => option.name || option.fullName || option.email || ""}
              isOptionEqualToValue={(option, value) => (option.userId || option.id) === (value.userId || value.id)}
              value={zmUsers.find((u) => (u.userId || u.id) === formik.values.incharge_user_id) || null}
              onChange={(_, selected) => {
                formik.setFieldValue("incharge_user_id", selected ? (selected.userId || selected.id || "") : "");
              }}
              onBlur={() => formik.setFieldTouched("incharge_user_id", true)}
              disabled={usersLoading}
              loading={usersLoading}
              noOptionsText={zmUsers.length === 0 ? "No ZM users available" : "No match found"}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder={usersLoading ? "Loading users..." : "Search and select incharge"}
                  error={formik.touched.incharge_user_id && Boolean(formik.errors.incharge_user_id)}
                  helperText={formik.touched.incharge_user_id && formik.errors.incharge_user_id}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                />
              )}
            />
          </Box>

          <Box display="flex" gap={2} flexDirection="row">
            <Button
              fullWidth
              variant="outlined"
              onClick={handleCancel}
              disabled={createLoading || updateLoading || zoneByIdLoading}
              sx={{
                borderRadius: 3,
                fontWeight: 600,
                p: "10px 0px",
                color: "#000",
                border: "1px solid #000",
                textTransform: "none",
              }}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={createLoading || updateLoading || zoneByIdLoading || !!duplicateError}
              sx={{
                borderRadius: 3,
                p: "10px 0px",
                fontWeight: 600,
                textTransform: "none",
                bgcolor: "#5B4CF5",
                boxShadow: "0px 8px 20px rgba(91,76,245,0.3)",
                "&:hover": { bgcolor: "#4a3de0" },
              }}
            >
              {createLoading || updateLoading ? (isEdit ? "Updating..." : "Submitting...") : (isEdit ? "Update Zone" : "Submit Data")}
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddZone;

AddZone.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  isEdit: PropTypes.bool,
  zoneId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};